import React, { FunctionComponent } from 'react'
import { useIntl } from 'react-intl'
import { LayoutChangeEvent } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import {
  ConsentBannerRecipients,
  getConsentBannerMessage,
} from '@lyrahealth-inc/shared-app-logic/src/features/consent/messages'
import { ConsentBannerStatus } from '@lyrahealth-inc/shared-app-logic/src/models/consent/Consent'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { LightbulbIcon } from '../../atoms/icons/LightbulbIcon'
import { XIcon } from '../../atoms/icons/XIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type InformedConsentSessionBannerProps = {
  /** Consent status to explain; the banner formats its own copy from it */
  status: ConsentBannerStatus
  /** Who still has to sign: the client, their caregiver, or both. Decides the copy. */
  recipients: ConsentBannerRecipients
  onDismiss: () => void
  /** Reports the rendered height so the layout can reserve exactly the space the text needs */
  onHeightChange?: (height: number) => void
}

const Container = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.components.inlineInfoBanner.background,
  padding: theme.spacing['24px'],
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
}))

const ContentContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
  paddingRight: theme.spacing['48px'],
}))

const DismissButton = styled(PressableOpacity)({
  position: 'absolute',
  right: 24,
})

export const InformedConsentSessionBanner: FunctionComponent<InformedConsentSessionBannerProps> = ({
  status,
  recipients,
  onDismiss,
  onHeightChange,
}) => {
  const { colors } = useTheme() as ThemeType
  const { inlineInfoBanner } = colors.components
  const intl = useIntl()
  const message = getConsentBannerMessage(intl, status, recipients)

  if (!message) return null

  return (
    <Container
      testID={tID('InformedConsentSessionBanner')}
      onLayout={(event: LayoutChangeEvent) => onHeightChange?.(event.nativeEvent.layout.height)}
    >
      <ContentContainer>
        <LightbulbIcon size={20} fillColor={inlineInfoBanner.infoIcon.fill} />
        <BodyText size={BodyTextSize.DEFAULT} text={message} color={inlineInfoBanner.text} />
      </ContentContainer>
      <DismissButton
        onPress={onDismiss}
        testID={tID('InformedConsentSessionBanner-dismiss')}
        accessibilityLabel={intl.formatMessage({
          defaultMessage: 'Dismiss informed consent banner',
          description:
            'Accessibility label for the button that dismisses the informed consent banner in a video session',
        })}
      >
        <XIcon size={20} fillColor={inlineInfoBanner.text} />
      </DismissButton>
    </Container>
  )
}
